// src/components/ChatAgent.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { useChat } from '../contexts/ChatContext.jsx';
import './ChatAgent.css';
const STATE_KEY = 'chatAgentState';

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
function clampState(saved) {
  const vpW = window.innerWidth || 1024;
  const vpH = window.innerHeight || 768;
  const minW = 300, maxW = 800, minH = 400, maxH = 1000;
  const width = clamp(Number(saved?.width ?? 400), minW, Math.min(maxW, vpW));
  const height = clamp(Number(saved?.height ?? 600), minH, Math.min(maxH, vpH));
  const maxX = Math.max(0, vpW - width);
  const maxY = Math.max(0, vpH - height);
  const x = clamp(Number(saved?.x ?? 250), 0, maxX);
  const y = clamp(Number(saved?.y ?? 50), 0, maxY);
  return { width, height, x, y };
}

/* ======================= Sortable/Paginated Table ======================= */
function TableView({ table }) {
  const [sortIndex, setSortIndex] = useState(-1);
  const [sortDir, setSortDir] = useState('asc');
  const [rows, setRows] = useState(table?.rows || []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setRows(table?.rows || []);
    setSortIndex(-1);
    setSortDir('asc');
    setPage(1);
  }, [table]);

  const sortBy = (idx) => {
    let dir = sortDir;
    if (sortIndex === idx) {
      dir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(dir);
    } else {
      setSortIndex(idx);
      dir = 'asc';
      setSortDir(dir);
    }
    const header = table.headers[idx] || '';
    const isDate = /date/i.test(header);
    const newRows = [...rows].sort((a, b) => {
      let av = a[idx], bv = b[idx];
      if (isDate) {
        const ad = av ? Date.parse(av) : 0;
        const bd = bv ? Date.parse(bv) : 0;
        return dir === 'asc' ? ad - bd : bd - ad;
      }
      const an = Number(av), bn = Number(bv);
      const aIsNum = !Number.isNaN(an) && String(av ?? '').trim() !== '';
      const bIsNum = !Number.isNaN(bn) && String(bv ?? '').trim() !== '';
      if (aIsNum && bIsNum) return dir === 'asc' ? an - bn : bn - an;
      return dir === 'asc'
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''));
    });
    setRows(newRows);
    setPage(1);
  };

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  const changePage = (next) => setPage(p => clamp(p + next, 1, totalPages));
  const goToPage = (p) => setPage(clamp(Number(p) || 1, 1, totalPages));

  if (!table || !Array.isArray(table.headers) || !Array.isArray(table.rows)) return null;
  return (
    <div className="chat-table-wrap">
      {table.title ? <div className="chat-table-title">{table.title}</div> : null}
      <div className="chat-table-scroll">
        <table className="chat-table">
          <thead>
            <tr>
              {table.headers.map((h, i) => (
                <th
                  key={i}
                  onClick={() => sortBy(i)}
                  style={{ cursor: 'pointer', whiteSpace: 'normal', wordWrap: 'break-word' }}
                  title="Click to sort"
                >
                  {h}
                  {sortIndex === i ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, rIdx) => (
              <tr key={`${page}-${rIdx}`}>
                {table.headers.map((_, cIdx) => (
                  <td key={cIdx} style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {row[cIdx] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="chat-table-pagination">
        <button onClick={() => changePage(-1)} disabled={page <= 1}>Prev</button>
        <span>Page</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={page}
          onChange={(e) => goToPage(e.target.value)}
        />
        <span>of {totalPages}</span>
        <button onClick={() => changePage(1)} disabled={page >= totalPages}>Next</button>
        <span className="sep" />
        <label>
          Rows per page:&nbsp;
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <span className="summary">
          &nbsp;•&nbsp;Showing {Math.min(total, start + 1)}–{Math.min(total, start + pageSize)} of {total}
        </span>
      </div>
    </div>
  );
}

/* =============================== Chat Agent =============================== */
const ChatAgent = () => {
  const navigate = useNavigate();
  const { isChatOpen, setIsChatOpen } = useChat();
  // Restrict to internal users only
  const isClient = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}');
      const v = u?.isclient ?? u?.isClient;
      return (v === true) || (Number(v) === 1) || (String(v).toLowerCase() === 'true') || (String(v) === '1');
    } catch { return false; }
  })();
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('chatAgentMode') || 'agent'; } catch { return 'agent'; }
  });
  useEffect(() => { try { localStorage.setItem('chatAgentMode', mode); } catch {} }, [mode]);
  const [dmList, setDmList] = useState([]);
  const [online, setOnline] = useState([]); // [{userid, fullname, login}]
  const [dmWith, setDmWith] = useState(null); // userid
  const [dmMsgs, setDmMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [userId, setUserId] = useState(0);
  const messagesRef = useRef(null);
  const hydratedRef = useRef(false);
  const sendMessageRef = useRef(null);
  const [size, setSize] = useState({ width: 400, height: 600 });
  const [position, setPosition] = useState({ x: 250, y: 50 });

  // ---- Dictation (Web Speech API) ----
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const [interim, setInterim] = useState(''); // live preview
  const sendingGuardRef = useRef(false); // block duplicate sends
  const heardSpeechRef = useRef(false); // only auto-send if true

  // Input caret/scroll-to-end
  const inputElRef = useRef(null);
  const scrollInputToEnd = useCallback(() => {
    const el = inputElRef.current;
    if (!el) return;
    const len = el.value.length;
    try { el.selectionStart = len; el.selectionEnd = len; } catch {}
    el.scrollLeft = el.scrollWidth;
  }, []);

  useEffect(() => {
    const t = setTimeout(scrollInputToEnd, 0);
    return () => clearTimeout(t);
  }, [input, interim, scrollInputToEnd]);

  // Latest state mirrors for recognition callbacks
  const inputStateRef = useRef('');
  const interimStateRef = useRef('');
  useEffect(() => { inputStateRef.current = input; }, [input]);
  useEffect(() => { interimStateRef.current = interim; }, [interim]);

  // Silence detection
  const SILENCE_MS = 2000;
  const silenceTimerRef = useRef(null);
  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);
  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      try { recognitionRef.current && recognitionRef.current.stop(); } catch {}
    }, SILENCE_MS);
  }, [clearSilenceTimer]);

  // Load user id
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserId(user.userid || 0);
    } catch { setUserId(0); }
  }, []);

  // Hydrate size/pos
  useEffect(() => {
    if (!isChatOpen || hydratedRef.current) return;
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const c = clampState(raw ? JSON.parse(raw) : {});
      setSize({ width: c.width, height: c.height });
      setPosition({ x: c.x, y: c.y });
    } catch {}
    hydratedRef.current = true;
  }, [isChatOpen]);

  // Persist size/pos
  useEffect(() => {
    if (!hydratedRef.current) return;
    localStorage.setItem(STATE_KEY, JSON.stringify({ ...size, ...position }));
  }, [size, position]);

  // Auto-scroll messages
  const scrollToBottom = useCallback(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => {
    const onResize = () => scrollToBottom();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [scrollToBottom]);

  // Load agent chat history or messages based on mode
  useEffect(() => {
    if (!isChatOpen || !userId) return;
    if (mode === 'agent') {
      (async () => {
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/api/agent/history?limit=150`, { headers: { 'x-user-id': String(userId) } });
          if (!res.ok) return;
          const j = await res.json();
          if (Array.isArray(j.data) && j.data.length) setMessages(j.data);
        } catch {}
      })();
    } else {
      (async () => {
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/api/messages/conversations`);
          if (res.ok) setDmList(await res.json());
        } catch {}
        try {
          const r2 = await fetch(`${import.meta.env.VITE_API_URL}/api/messages/online`);
          const j2 = await r2.json().catch(()=>({ online: [] }));
          if (Array.isArray(j2?.online)) setOnline(j2.online);
        } catch {}
      })();
    }
  }, [isChatOpen, userId, mode]);

  // SSE subscription for presence + live messages (DMs)
  useEffect(() => {
    if (!isChatOpen || mode !== 'dm') return;
    const src = new EventSource(`${import.meta.env.VITE_API_URL}/api/messages/stream`, { withCredentials: true });
    src.addEventListener('presence', (e) => {
      try { const d = JSON.parse(e.data || '{}'); if (Array.isArray(d.online)) setOnline(d.online); } catch {}
    });
    src.addEventListener('message', (e) => {
      try {
        const m = JSON.parse(e.data || '{}');
        // If this message is for the active thread, append
        if (m && ((m.sender_id === userId && m.recipient_id === dmWith) || (m.sender_id === dmWith && m.recipient_id === userId))) {
          setDmMsgs(prev => [...prev, m]);
        }
        // Update conversations list ordering by bumping counterpart to top if needed
        const otherId = (m.sender_id === userId) ? m.recipient_id : m.sender_id;
        setDmList(prev => {
          const exists = prev.find(x => x.userid === otherId);
          const others = prev.filter(x => x.userid !== otherId);
          const basic = exists || online.find(x => x.userid === otherId) || { userid: otherId, fullname: `User #${otherId}`, login: '' };
          return [basic, ...others];
        });
      } catch {}
    });
    src.onerror = () => { /* auto-reconnect by browser */ };
    return () => { try { src.close(); } catch {} };
  }, [isChatOpen, mode, dmWith, userId]);

  /* ---------------------- sendMessage ---------------------- */
  const sendMessage = useCallback(async (overrideText) => {
    const composed = (overrideText != null ? overrideText :
      `${input}${interim ? (input && !input.endsWith(' ') ? ' ' : '') + interim : ''}`
    ).trim();
    if (!composed || !userId) {
      console.log('[ChatAgent] Send aborted: composed=', composed, 'userId=', userId);
      return;
    }
    sendingGuardRef.current = true;
    const newMessages = [...messages, { text: composed, sender: 'user' }];
    setMessages(newMessages);
    setInput('');
    setInterim('');
    try {
      const res = await fetch('http://localhost:5050/api/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': String(userId) },
        body: JSON.stringify({ message: composed }),
      });
      const data = await res.json();
      console.log('[ChatAgent] API response:', JSON.stringify(data, null, 2));
      if (res.ok) {
        // Navigate
        if (data.action && data.action.type === 'navigate' && data.action.to) {
          const botMsg = { text: data.reply || 'OK', sender: 'bot' };
          setMessages([...newMessages, botMsg]);
          navigate(data.action.to);
          sendingGuardRef.current = false;
          return;
        }
        // Create Receipt
        if (data.action && data.action.type === 'createReceipt' && data.action.payload) {
          const botMsg = { text: data.reply || 'OK', sender: 'bot' };
          setMessages([...newMessages, botMsg]);
          navigate('/receipts');
          setTimeout(() => {
            const payload = data.action.payload;
            console.log('[ChatAgent] Dispatching agent:createReceipt:', {
              userId,
              warehouseId: payload.warehouseid,
              clientId: payload.clientid,
              warehouseName: payload.resolvedNames?.warehouse || payload.warehouseName || '',
              clientName: payload.resolvedNames?.client || payload.clientName || '',
              po: payload.po || '',
              entry: payload.entry || '',
              notes: payload.notes || ''
            });
            window.dispatchEvent(new CustomEvent('agent:createReceipt', {
              detail: {
                userId,
                warehouseId: payload.warehouseid || null,
                clientId: payload.clientid || null,
                warehouseName: payload.resolvedNames?.warehouse || payload.warehouseName || '',
                clientName: payload.resolvedNames?.client || payload.clientName || '',
                po: payload.po || '',
                entry: payload.entry || '',
                notes: payload.notes || ''
              }
            }));
          }, 120);
          sendingGuardRef.current = false;
          return;
        }
        // Create Order
        if (data.action && data.action.type === 'createOrder' && data.action.payload) {
          const botMsg = { text: data.reply || 'OK', sender: 'bot' };
          setMessages([...newMessages, botMsg]);
          navigate('/orders');
          setTimeout(() => {
            const payload = data.action.payload;
            console.log('[ChatAgent] Dispatching agent:createOrder:', {
              userId,
              warehouseId: payload.warehouseid,
              clientId: payload.clientid,
              orderdate: payload.orderdate,
              po: payload.po || '',
              notes: payload.notes || ''
            });
            window.dispatchEvent(new CustomEvent('agent:createOrder', {
              detail: {
                userId,
                warehouseId: payload.warehouseid || null,
                clientId: payload.clientid || null,
                orderdate: payload.orderdate || null,
                po: payload.po || '',
                notes: payload.notes || ''
              }
            }));
          }, 120);
          sendingGuardRef.current = false;
          return;
        }
        // Default/table reply
        const botMsg = data.table
          ? { text: data.reply || '', sender: 'bot', table: data.table }
          : { text: data.reply || 'OK', sender: 'bot' };
        if (data.csv) botMsg.csv = data.csv;
        if (data.json) botMsg.json = data.json;
        setMessages([...newMessages, botMsg]);
      } else {
        setMessages([...newMessages, { text: data.error || 'Error occurred', sender: 'bot' }]);
      }
    } catch {
      setMessages([...newMessages, { text: 'Failed to connect to chat agent', sender: 'bot' }]);
    } finally {
      sendingGuardRef.current = false;
    }
  }, [input, interim, userId, messages, navigate]);

  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  /* ---------------------- Dictation: live typing + robust autosend ---------------------- */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SR);
  }, []);

  const ensureRecognizer = useCallback(() => {
    if (recognitionRef.current) return recognitionRef.current;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const recog = new SR();
    recog.lang = 'en-US';
    recog.interimResults = true; // live words
    recog.continuous = true; // keep session alive; we'll stop on silence
    recog.onstart = () => {
      setInterim('');
      setIsListening(true);
      sendingGuardRef.current = false;
      heardSpeechRef.current = false; // reset speech flag
      // DO NOT arm silence timer here; wait until we actually hear something
    };
    recog.onerror = (e) => {
      console.warn('Speech error:', e.error);
      setIsListening(false);
      setInterim('');
      clearSilenceTimer();
    };
    recog.onresult = (event) => {
      let interimNow = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = (event.results[i][0]?.transcript || '');
        if (event.results[i].isFinal) finalChunk += transcript;
        else interimNow += transcript;
      }
      if (interimNow || finalChunk) {
        heardSpeechRef.current = true; // we heard real speech
        armSilenceTimer(); // re-arm silence on any activity
      }
      // show live preview
      setInterim(interimNow);
      // commit finalized chunk(s)
      if (finalChunk) {
        const add = finalChunk.trim();
        if (add) {
          setInput(prev => (prev ? (prev.endsWith(' ') ? prev : prev + ' ') : '') + add);
          setTimeout(scrollInputToEnd, 0);
        }
      }
    };
    // helpful signals, but not always fired in all environments
    recog.onsoundstart = () => { armSilenceTimer(); };
    recog.onsoundend = () => { armSilenceTimer(); };
    // When the engine ends (because we called stop() on silence)
    recog.onend = () => {
      setIsListening(false);
      clearSilenceTimer();
      const committed = inputStateRef.current || '';
      const preview = interimStateRef.current || '';
      const composed = `${committed}${preview ? (committed && !committed.endsWith(' ') ? ' ' : '') + preview : ''}`.trim();
      setInterim('');
      // Only auto-send if we actually heard speech and there's content
      if (heardSpeechRef.current && composed && !sendingGuardRef.current) {
        sendingGuardRef.current = true;
        if (sendMessageRef.current) sendMessageRef.current(composed);
      }
    };
    recognitionRef.current = recog;
    return recog;
  }, [armSilenceTimer, clearSilenceTimer, scrollInputToEnd]);

  const toggleDictation = useCallback(() => {
    const recog = ensureRecognizer();
    if (!recog) {
      alert('Voice dictation is not supported in this browser.');
      return;
    }
    try {
      if (isListening) {
        clearSilenceTimer();
        recog.stop();
      } else {
        setInterim('');
        sendingGuardRef.current = false;
        heardSpeechRef.current = false;
        recog.start();
      }
    } catch (err) {
      console.warn('Speech start/stop failed', err);
      setIsListening(false);
      clearSilenceTimer();
    }
  }, [ensureRecognizer, isListening, clearSilenceTimer]);

  /* ---------------------- Clear input only ---------------------- */
  const clearInput = () => {
    setInterim('');
    setInput('');
    if (isListening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      setIsListening(false);
    }
  };

  /* ---------------------- Drag / Resize ---------------------- */
  const startDragging = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX, startY = e.pageY;
    const startLeft = position.x, startTop = position.y;
    const handleMouseMove = (e2) => {
      const newX = startLeft + (e2.pageX - startX);
      const newY = startTop + (e2.pageY - startY);
      const vpW = window.innerWidth || 1024, vpH = window.innerHeight || 768;
      const maxX = Math.max(0, vpW - size.width);
      const maxY = Math.max(0, vpH - size.height);
      setPosition({ x: clamp(newX, 0, maxX), y: clamp(newY, 0, maxY) });
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { once: true });
  }, [position.x, position.y, size.width, size.height]);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX, startY = e.pageY;
    const startWidth = size.width, startHeight = size.height;
    const handleMouseMove = (e2) => {
      const newW = startWidth + (e2.pageX - startX);
      const newH = startHeight + (e2.pageY - startY);
      const vpW = window.innerWidth || 1024, vpH = window.innerHeight || 768;
      const w = clamp(newW, 300, Math.min(800, vpW));
      const h = clamp(newH, 400, Math.min(1000, vpH));
      const maxX = Math.max(0, vpW - w), maxY = Math.max(0, vpH - h);
      setSize({ width: w, height: h });
      setPosition(p => ({ x: clamp(p.x, 0, maxX), y: clamp(p.y, 0, maxY) }));
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { once: true });
  }, [size.width, size.height]);

  if (!isChatOpen || isClient) return null;

  // Live value the user sees: committed + interim
  const composedInput = `${input}${interim ? (input && !input.endsWith(' ') ? ' ' : '') + interim : ''}`;

  return (
    <div className="chat-overlay">
      <div
        className="chat-modal"
        style={{
          width: `${size.width}px`,
          height: `${size.height}px`,
          left: `${position.x}px`,
          top: `${position.y}px`,
          position: 'absolute',
          zIndex: 2000,
        }}
      >
        <div className="chat-header" onMouseDown={startDragging}>
          <h3>Easy3PL Chat</h3>
          <div style={{ display:'flex', gap:6 }}>
            <button className={`btn-mic listening`} title="Agent">Agent</button>
          </div>
          <button className="chat-close" onClick={() => setIsChatOpen(false)}>×</button>
        </div>
        <div className="chat-messages" ref={messagesRef}>
          {mode === 'agent' ? (
            messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.sender}`}>
                {msg.table ? (<><div style={{ marginBottom: 6 }}>{msg.text}</div><TableView table={msg.table} /></>) : (<span>{msg.text}</span>)}
              </div>
            ))
          ) : null}
        </div>
        <div className="chat-input">
          <input
            ref={inputElRef}
            type="text"
            value={composedInput}
            onChange={(e) => {
              // If user edits while interim visible, treat entire new value as committed input
              setInput(e.target.value);
              setInterim('');
              setTimeout(scrollInputToEnd, 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // stop mic if running, then send composed text
                if (isListening && recognitionRef.current) {
                  try { recognitionRef.current.stop(); } catch {}
                }
                sendMessage();
              }
            }}
            placeholder={'Speak or type your message…'}
          />
          {/* Dictation (Mic) toggle */}
          <button
            className={`btn-mic ${isListening ? 'listening' : ''}`}
            onClick={toggleDictation}
            title={speechSupported ? (isListening ? 'Stop dictation' : 'Start dictation') : 'Dictation not supported'}
            disabled={!speechSupported}
            aria-pressed={isListening}
            aria-label="Dictate"
          >
            {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>
          <button onClick={() => sendMessage()}>Send</button>
          <button className="btn-clear" onClick={() => { setInput(''); setInterim(''); }}>Clear</button>
        </div>
        <div className="resize-handle" onMouseDown={startResizing} />
      </div>
    </div>
  );
};

export default ChatAgent;
